<template>
  <div class="oidc-return-page">
    <!-- Shared header shows branding only; the sign-in link is suppressed in favour of the page-local nav below. -->
    <CwePageHeader :authenticationRequired="true" />

    <div id="OIDCReturnPage">
      <!-- Page-local header nav: restart sign-in, or complete the login this OIDC return began. -->
      <nav class="diag-header-nav">
        <a class="diag-header-link" :href="signInUrl">Return to Sign-In</a>
        <span class="diag-header-divider">|</span>
        <a class="diag-header-link" :href="dashboardUrl">Complete Login</a>
      </nav>

      <h1 class="diag-title">SENSITIVE: OIDC Return Diagnostics</h1>
      <p class="diag-warning">
        This report contains raw tokens and personal data. Handle accordingly; do not forward.
      </p>

      <!-- Single-use banner: once the email is attempted it reports success/failure and that the ticket is spent. -->
      <div
        v-if="submitted"
        class="diag-banner"
        :class="submitSuccess ? 'diag-banner--success' : 'diag-banner--error'"
        role="alert"
      >
        {{ bannerMessage }}
      </div>

      <!-- Permission form: submit (email) is disabled until the reviewer opts in, and locked after one attempt. -->
      <form class="diag-permission" @submit.prevent="submit">
        <label class="diag-permission-label">
          <input type="checkbox" v-model="permissionGranted" :disabled="submitting || submitted" />
          <span>
            I understand this report contains raw tokens and personal data, and I authorize emailing it to the
            configured recipients.
          </span>
        </label>
        <button type="submit" class="diag-submit-btn" :disabled="!permissionGranted || submitting || submitted">
          Email report
        </button>
      </form>

      <div v-if="loadError" class="diag-warning diag-load-error">{{ loadError }}</div>

      <template v-else-if="report">
        <p class="diag-captured">Captured (UTC): {{ report.capturedAtUtc }}</p>

        <h2 class="diag-group">SUMMARY</h2>

        <!-- When the identity provider posts an OAuth/OIDC error back to the callback (error / error_description /
             error_uri in the request body), surface it at the very top of the summary so the failure reason is the
             first thing seen, ahead of any captured tokens. -->
        <div v-if="postError" class="diag-error">
          <h3 class="diag-section">Identity provider error returned to callback</h3>
          <table class="diag-table">
            <tbody>
              <tr v-for="(pair, i) in postError.fields" :key="'errfield' + i">
                <th>{{ pair.key }}</th><td class="diag-error-value">{{ nz(pair.value) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 class="diag-section">All captured JWT tokens (deduped, signatures neutered)</h3>
        <template v-if="capturedJwtTokens.length > 0">
          <div v-for="(tok, i) in capturedJwtTokens" :key="'tok' + i" class="diag-token">
            <p class="diag-token-label">{{ tok.label }}</p>
            <p class="diag-raw">{{ nz(tok.raw) }}</p>
            <table v-if="tok.claims.length > 0" class="diag-table">
              <tbody>
                <tr v-for="(pair, j) in tok.claims" :key="'tokclaim' + i + '_' + j">
                  <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
        <p v-else class="diag-note">(none)</p>

        <h3 class="diag-section">Auth cookie claims</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.responseAuthCookieClaims" :key="'authclaim' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>

        <h2 class="diag-group">REQUEST</h2>

        <h3 class="diag-section">Request Headers</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.requestHeaders" :key="'reqheader' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>

        <h3 class="diag-section">Request Cookies</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.requestCookies" :key="'reqcookie' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>

        <h3 class="diag-section">Request Form Data</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.requestForm" :key="'reqform' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>

        <h2 class="diag-group">RESPONSE</h2>

        <h3 class="diag-section">Response Headers</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.responseHeaders" :key="'resheader' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>

        <h3 class="diag-section">Response Cookies</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.responseCookies" :key="'rescookie' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>

        <h3 class="diag-section">Response Form Data</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.responseForm" :key="'resform' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>

        <h2 class="diag-group">DETAILS</h2>

        <h3 class="diag-section">Verification (re-derived from inputs)</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.verification" :key="'ver' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>

        <h3 class="diag-section">B2C token-endpoint response fields (auth-code exchange)</h3>
        <table class="diag-table">
          <tbody>
            <tr v-for="(pair, i) in report.tokenEndpointResponse" :key="'tep' + i">
              <th>{{ pair.key }}</th><td>{{ nz(pair.value) }}</td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
  import * as Vue from 'vue';
  import CwePageHeader from '../components/CwePageHeader.vue';
  import WaitForAppDependencies from '../services/AppDependencyService';
  import FixProxyUrl from '../services/FixProxyUrl';
  import { getOIDCReturnDiagnostics, submitOIDCReturnDiagnostics } from '@/api/endpoints/diagnosticsWeb';
  import { AuthDiagnosticReport, StringPair } from '@/types/webContracts';

  interface CapturedToken {
    label: string;
    raw: string;
    claims: StringPair[];
  }

  export default Vue.defineComponent({
    name: 'OIDCReturnPage',
    components: {
      CwePageHeader,
    },
    data() {
      return {
        ticketId: '',
        report: null as AuthDiagnosticReport | null,
        loadError: '',
        permissionGranted: false,
        submitting: false,
        submitted: false,
        submitSuccess: false,
        bannerMessage: ''
      }
    },
    created: async function () {
      await WaitForAppDependencies();
      this.ticketId = this.$route.params.ticketId?.toString() ?? '';
      await this.loadReport();
    },
    computed: {
      // Restart the whole sign-in flow (proxy-aware, same helper the shared header uses).
      signInUrl(): string {
        return FixProxyUrl('/login');
      },
      // Complete the login this OIDC return began by navigating to the dashboard; the auth cookie minted
      // during the callback carries the session, so a full navigation lands the user authenticated.
      dashboardUrl(): string {
        return FixProxyUrl('/dashboard');
      },
      // Detect an OAuth/OIDC error the identity provider posted back to the callback. The request body carries
      // the standard `error` field (plus `error_description` / `error_uri`) on a failed federation; when `error`
      // is present and non-empty, expose those fields so the summary can lead with the failure reason.
      postError(): { fields: StringPair[] } | null {
        const r = this.report;
        if (r == null)
          return null;

        const fields = r.requestForm.filter(p => p.key === 'error' || p.key.startsWith('error_'));
        const hasError = fields.some(p => p.key === 'error' && p.value != null && p.value !== '');
        return hasError ? { fields } : null;
      },
      // Aggregate every captured JWT (front-channel id_token, exchanged id_token, idp_access_token) into a
      // single list, deduped by their signature-neutered raw value so a token captured in more than one place
      // is shown once with all of its source labels. The backend still models each token separately.
      capturedJwtTokens(): CapturedToken[] {
        const r = this.report;
        if (r == null)
          return [];

        const sources: { label: string; raw: string | null; claims: StringPair[] }[] = [
          { label: 'id_token (front-channel, request)', raw: r.idTokenRaw, claims: r.idTokenClaims },
          { label: 'id_token (exchanged from auth code)', raw: r.exchangedIdTokenRaw, claims: r.exchangedIdTokenClaims },
          { label: 'idp_access_token (federated IdP)', raw: r.idpAccessTokenRaw, claims: r.idpAccessTokenClaims }
        ];

        const byRaw = new Map<string, CapturedToken>();
        for (const s of sources) {
          if (s.raw == null || s.raw === '')
            continue;

          const existing = byRaw.get(s.raw);
          if (existing) {
            existing.label += `, ${s.label}`;
            if (existing.claims.length === 0 && s.claims.length > 0)
              existing.claims = s.claims;
          } else {
            byRaw.set(s.raw, { label: s.label, raw: s.raw, claims: s.claims ?? [] });
          }
        }

        return Array.from(byRaw.values());
      }
    },
    methods: {
      // Renders empty/absent values the same way the email report does, for consistency.
      nz(value: string | null | undefined): string {
        return value == null || value === '' ? '(none)' : value;
      },
      async loadReport() {
        if (!this.ticketId) {
          this.loadError = 'Diagnostics report not found or already viewed.';
          return;
        }

        const result = await getOIDCReturnDiagnostics(this.ticketId);
        if (result == null) {
          this.loadError = 'Diagnostics report not found or already viewed.';
          return;
        }

        this.report = result;
      },
      async submit() {
        if (!this.permissionGranted || this.submitting || this.submitted)
          return;

        this.submitting = true;

        const sent = await submitOIDCReturnDiagnostics(this.ticketId);

        // The ticket is single-use, so any completed attempt spends it — lock the form and explain that the
        // page and email are now unavailable and a fresh sign-in is required to run diagnostics again.
        this.submitSuccess = sent;
        this.bannerMessage = sent
          ? 'This report has been emailed to the configured recipients. For your protection this page is ' +
            'single-use: the report can no longer be viewed here and the email cannot be sent again. To run ' +
            'diagnostics again, sign in again.'
          : 'The report could not be emailed — it may have already been submitted. This page is single-use: no ' +
            'further attempts to view it or resend the email can be made. To try again, sign in again.';
        this.submitting = false;
        this.submitted = true;
      }
    },
  })
</script>

<style lang="scss">
  // Adopts the dashboard aesthetic: the branded diamonds background sits behind a centred white content panel,
  // reusing the same design tokens ($maxInnerPageWidth, $primary_color, $dark_accent_color, ...) as DashboardPage.
  .oidc-return-page {
    background-image: url('/public/img/zbedge-diamonds-background.png');
    background-size: cover;
    background-position: center;
    display: flex;
    flex: 2;
    flex-direction: column;
    justify-content: flex-start;
    min-height: calc(100vh - 76px);
  }

  #OIDCReturnPage {
    // Centred content panel matching the dashboard's inner container width.
    width: $maxInnerPageWidth;
    max-width: 100%;
    margin: calc(#{$standard_space} * 2) auto;
    padding: calc(#{$standard_space} * 2);
    text-align: left;
    color: $dark_accent_color;

    // White card so the report stays readable over the branded background.
    background: $white;
    border-radius: $area_border_radius;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);

    // Page-local header nav (Return to Sign-In | Complete Login), right-aligned like the shared header.
    .diag-header-nav {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 12px;
      margin: 0 0 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid $medium_light_gray;
      font-size: 1rem;
    }

    .diag-header-link {
      color: $primary_color;
      text-decoration: none;

      &:hover {
        color: $primary_color_active;
        text-decoration: underline;
      }
    }

    .diag-header-divider {
      color: $medium_light_gray;
      font-weight: bold;
    }

    // Title inherits the app's standard heading colour/size from the global h1 rule; only the spacing is tuned.
    .diag-title {
      margin: 0 0 4px;
    }

    // Single-use submit banner: success/failure notice shown above the form once the email is attempted.
    .diag-banner {
      margin: 16px 0;
      padding: 12px 16px;
      border: 1px solid;
      border-radius: $area_border_radius;
      font-size: 1rem;
      line-height: 1.4;
    }

    .diag-banner--success {
      color: #1b5e20;
      background: #e8f5e9;
      border-color: #1b5e20;
    }

    .diag-banner--error {
      color: $error_color;
      background: #fdecea;
      border-color: $error_color;
    }

    .diag-warning {
      font-size: 0.8rem;
      color: $error_color;
      margin: 0 0 12px;
    }

    .diag-load-error {
      font-size: 1rem;
      margin-top: 16px;
    }

    .diag-permission {
      display: flex;
      align-items: center;
      margin: 12px 0 20px;
      padding: 12px;
      border: 1px solid $medium_light_gray;
      border-radius: $area_border_radius;
      background: $light_gray;

      // Checkbox flush-left, permission text immediately to its right, on a single row.
      .diag-permission-label {
        display: flex;
        align-items: center;
        font-size: 1.5rem;

        input {
          width: 1.5rem;
          height: 1.5rem;
          margin-right: 8px;
          flex-shrink: 0;
        }
      }

      // Push the submit button to the right edge of the form row.
      .diag-submit-btn {
        margin-left: auto;
      }

      .diag-submit-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .diag-captured {
      font-size: 0.8rem;
      color: $faint_text;
      margin: 0 0 16px;
    }

    // Group headings mirror the dashboard's .sectionHeader: dark accent text over a light underline.
    .diag-group {
      font-size: 1.3rem;
      font-weight: bold;
      letter-spacing: 0.05em;
      color: $dark_accent_color;
      margin: 32px 0 8px;
      border-bottom: 2px solid $medium_light_gray;
      padding-bottom: 4px;
    }

    .diag-section {
      font-size: 1rem;
      font-weight: bold;
      color: $dark_accent_color;
      margin: 20px 0 6px;
      border-bottom: 1px solid $medium_light_gray;
      padding-bottom: 2px;
    }

    .diag-note {
      font-size: 0.8rem;
      margin: 2px 0;
    }

    // Provider-error card at the top of the summary: error-tinted panel; error_description keeps the provider's
    // own line breaks (Correlation ID / Timestamp on their own lines) via pre-wrap.
    .diag-error {
      margin: 8px 0 4px;
      padding: 12px;
      border: 1px solid $error_color;
      border-radius: $area_border_radius;
      background: #fdecea;

      .diag-section {
        margin-top: 0;
        color: $error_color;
        border-bottom-color: $error_color;
      }

      .diag-error-value {
        white-space: pre-wrap;
        color: $error_color;
      }
    }

    .diag-token {
      margin: 0 0 14px;
    }

    .diag-token-label {
      font-size: 0.85rem;
      font-weight: bold;
      color: $dark_accent_color;
      margin: 6px 0 2px;
    }

    // Neutered token value: monospace, wraps, shown above its decoded claims (mirrors the email layout).
    .diag-raw {
      font-family: Consolas, 'Courier New', monospace;
      font-size: 0.75rem;
      word-break: break-all;
      margin: 2px 0 6px;
      color: $faint_text;
    }

    .diag-table {
      border-collapse: collapse;
      width: 100%;
      font-size: 0.8rem;

      th, td {
        border: 1px solid $medium_light_gray;
        padding: 6px;
        vertical-align: top;
        word-break: break-all;
        text-align: left;
      }

      th {
        font-weight: bold;
        width: 240px;
        background: $light_gray;
      }
    }

    // Mobile: stack each key/value row into its own card, and let the permission form flow vertically.
    @media screen and (max-width: 600px) {
      margin: $standard_space;
      padding: $standard_space;

      .diag-header-nav {
        justify-content: flex-start;
        flex-wrap: wrap;
      }

      .diag-permission {
        flex-direction: column;
        align-items: flex-start;

        .diag-submit-btn {
          margin-left: 0;
          margin-top: 12px;
        }
      }

      .diag-table {
        // Each row becomes a bordered card; key sits above its value, both full-width.
        tr {
          display: block;
          border: 1px solid $medium_light_gray;
          margin-bottom: 10px;
        }

        th, td {
          display: block;
          width: 100%;
          border: none;
        }

        th {
          background: $light_gray;
          border-bottom: 1px solid $medium_light_gray;
        }
      }
    }
  }
</style>
